import { useCallback } from 'react';
import { type ConsultingOffer, type PreparationStage } from '../../types';
import { type CreateAccountResponse, type Schedule } from '@accounts';
import { useStageAnswer, type AnswerStep } from '../../stage/hooks/useStageAnswer';
import { useScheduleConnection, type ConnectionStep } from '../../schedule/hooks/useScheduleConnection';

export type PreparationStep = AnswerStep | ConnectionStep;

interface UsePreparationFlowOptions {
  stage: PreparationStage | null;
  consulting?: ConsultingOffer | null;
  consultingRequested?: boolean;
  onCompleted?: () => void;
  onDone?: () => void;
}

export interface UsePreparationFlowReturn {
  step: PreparationStep;
  stage: PreparationStage | null;
  chooseStage: (stage: PreparationStage) => void;
  connected: (response: CreateAccountResponse) => void;
  chooseSchedule: (schedule: Schedule) => void;
  schedulesFound: (schedules: Schedule[]) => void;
  cancelActivation: () => void;
  complete: () => void;
  restart: () => void;
  stageSaveFailed: boolean;
  retryStageSave: () => void;
}

export function usePreparationFlow({
  stage,
  consulting,
  consultingRequested,
  onCompleted,
  onDone,
}: UsePreparationFlowOptions): UsePreparationFlowReturn {
  const answer = useStageAnswer({ stage, consulting, consultingRequested });
  const connection = useScheduleConnection({
    onCompleted,
    onDone: () => {
      answer.reset();
      onDone?.();
    },
  });

  const connected = useCallback(
    (response: CreateAccountResponse) => {
      answer.promoteStage('portal_ready');
      connection.connected(response);
    },
    [answer, connection]
  );

  const restart = useCallback(() => {
    answer.reset();
    connection.reset();
  }, [answer, connection]);

  return {
    step: connection.step ?? answer.step,
    stage: answer.stage,
    chooseStage: answer.chooseStage,
    connected,
    chooseSchedule: connection.chooseSchedule,
    schedulesFound: connection.schedulesFound,
    cancelActivation: connection.cancelActivation,
    complete: connection.complete,
    restart,
    stageSaveFailed: answer.saveFailed,
    retryStageSave: answer.retrySave,
  };
}
